import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { StyleRoot } from 'radium';

import './Finance.scss';
import PersonalizationContainer from '../../containers/PersonalizationContainer';
import NavigationBar from '../NavigationBar';
import Currency from '../Currency';
import bem from '../../utils/bem';
import RangeSlider from '../RangeSlider';
import Carousel from '../Carousel';
import Skeleton from '../Skeleton';
import { ANIMATIONS, MINICUOTAS_NAME } from '../../utils/constants';
import { chunk } from '../../utils/helpers';
import useVersion from '../../utils/hooks/useVersion';
import FinanceOption from './FinanceOption';
import useCar from '../../utils/hooks/useCar';
const MAX_FINANCING_PERCENTAGE = 80;

/**
 * Returns the max amount to finance
 * @param {Number} price
 * @returns {Number}
 */
const getMaxAmount = price => price * (MAX_FINANCING_PERCENTAGE / 100);

// /**
//  * Returns the min amount to finance
//  * @param {Number} price
//  * @returns {Number}
//  */
// const getMinAmount = price => price * (MAX_FINANCING_PERCENTAGE / 20);

const validateAmount = (amount, price) => {
  const maxAmount = getMaxAmount(price);
  return amount <= maxAmount;
};


const FinanceOptions = ({
  financeOptions,
  prime,
  financingAmount,
  selectFinancingOption,
  selectedFinancingOptionsNumber,
  resetAdd,
  plazo,
  car,
  amountOriginal,
}) => {
  const { hasPromo } = useVersion();
  const { finalPrice, minicuotaInsurancePrice, insurancePrice, primeSecure, carPrice } = useCar(car);
  let financeOptionsNew = financeOptions;
  let capital = null;
  if (hasPromo) {
    capital = financeOptionsNew.find(x => x.post_type === MINICUOTAS_NAME);
    financeOptionsNew = financeOptionsNew.filter(x => x.post_type !== MINICUOTAS_NAME);
    //financeOptionsNew.unshift(capital);
  } else {
    financeOptionsNew = financeOptionsNew.filter(x => x.post_type !== MINICUOTAS_NAME);
  }
  
  return (
  <div className={bem('finance-options', { shouldShowMinicuotas: !!capital})}>
    {capital && (
      <FinanceOption
      shouldUseCapitalStyle={capital.post_type === MINICUOTAS_NAME && hasPromo}
      resetAdd={resetAdd}
      key={capital.post_type}
      financeOption={capital}
      prime={prime}
      financingAmount={financingAmount}
      selectFinancingOption={selectFinancingOption}
      selectedFinancingOptionsNumber={selectedFinancingOptionsNumber}
      plazo={plazo}
      hasPromo={hasPromo}
      option={capital}
      finalPrice={finalPrice}
      minicuotaInsurance={minicuotaInsurancePrice}
      insurancePrice={insurancePrice}
      primeSecure={primeSecure}
      carPrice={carPrice}
      amountOriginal={amountOriginal}
    />
    )}
    <Carousel>
      {chunk(financeOptionsNew, capital ? 3 : 4).map((group, i) => {
        return (
          <div key={'group-'+ i}>
            <div className="finance-options__group">
              {group.map(option => (
                <FinanceOption
                  shouldUseCapitalStyle={option.post_type === MINICUOTAS_NAME && hasPromo}
                  resetAdd={resetAdd}
                  key={option.post_type}
                  financeOption={option}
                  prime={prime}
                  financingAmount={financingAmount}
                  selectFinancingOption={selectFinancingOption}
                  selectedFinancingOptionsNumber={selectedFinancingOptionsNumber}
                  plazo={plazo}
                  hasPromo={hasPromo}
                  option={option}
                  finalPrice={finalPrice}
                  minicuotaInsurance={minicuotaInsurancePrice}
                  insurancePrice={insurancePrice}
                  primeSecure={primeSecure}
                  carPrice={carPrice}
                />
              ))}
            </div>
          </div>
          
        )
      })}
      
    </Carousel>
  </div>
)
};

const Loader = () => (
  <div className="insurance__skeleton">
    <div className="insurance__skeleton-row skeleton">
      <Skeleton>
        <div className="insurance__skeleton-bone skeleton__box" />
      </Skeleton>
    </div>
    <div className="insurance__skeleton-row">
      <Skeleton>
        <div className="insurance__skeleton-bone skeleton__box" />
      </Skeleton>
    </div>
  </div>
);

const calculateAmount = (price, prime) => price - price * (prime / 100);

const calculatePrimePercentage = (price, amount) =>
  ((price - amount) * 100) / price;

const Finance = ({
  financeOptions,
  isLoading,
  price,
  prime,
  plazo,
  changePrimePercentage,
  selectFinancingOption,
  colorCode,
  changePlazo,
  car,
  carPrice,
}) => {
  const { tipo, modelo, color, version, anio } = useParams();
  const [amount, setAmount] = useState(calculateAmount(price, prime));
  const [amountOriginal, setAmountOriginal] = useState(calculateAmount(carPrice, prime));
  const [manuallyChangedAmount, setManuallyChangedAmount] = useState(false);
  const selectedFinancingOptionsNumber = (financeOptions || []).filter(
    option => option.isSelected
  )?.length;

  const { hasActivePromo } = useVersion();

  useEffect(() => {
    setAmount(calculateAmount(price, prime));
    setAmountOriginal(calculateAmount(carPrice, 20));
  }, [carPrice, price, prime]);

  useEffect(() => {
    if (manuallyChangedAmount) {
      changePrimePercentage(calculatePrimePercentage(price, amount));
      setManuallyChangedAmount(false);
    }
  }, [amount, changePrimePercentage, manuallyChangedAmount, price, prime]);

  const { hasPromo } = useVersion();

  const plazoMatch = {
    48: 1,
    60: 2,
    72: 3,
    84: 4,
    96: 5
  }

  const executeSelect = (option) => {
    if (option?.post_type === MINICUOTAS_NAME && hasPromo) {
      changePlazo(96);
      changePrimePercentage(20);
    }
    selectFinancingOption(option);
  }
  return (
    <PersonalizationContainer
      className="finance-personalization"
      title="FINANCIAMIENTO"
      subTitle="Ponemos a tu disposición planes de financiamiento con las mejores entidades bancarias del país. Escogé tu cuota aproximada aquí:"
    >
      <div className="finance" style={{ '--color': colorCode }}>
        <NavigationBar
          back={`/${tipo}/${modelo}/${version}/${anio}/${color}/seguros/`}
          next={`/${tipo}/${modelo}/${version}/${anio}/${color}/resumen/`}
        />
      {isLoading ? (
        <Loader />
      ) : (
        <StyleRoot>
          <div className="title-wrapper" style={{marginTop: '2.11rem'}}>
            <h6 className="title-button color">FINANCIAMIENTO PARA VOS</h6>
          </div>
          <span className="finance__instructions">Seleccioná el financiamiento que vaya más con vos.</span>
          <div className="finance__content" style={ANIMATIONS.fadeIn}>
            <div className="finance__section finance__section--financing">
              <p className="finance__subtitle">
              Calcula la opción que se ajuste a vos, escogé las opciones para así brindarte el financiamiento que mejor se adapte a tus necesidades.
              </p>
              <div className={bem('finance__prime-amount', {
                hasPromo: !!hasActivePromo
              })}>
                <div className="finance__input">
                  <span className="finance__subtitle-snd">
                    Monto a financiar :
                  </span>
                  <Currency
                    displayType="input"
                    className="finance__currency-input"
                    placeholder="Escribe el monto"
                    value={amount}
                    isAllowed={({ value }) => {
                      const newAmount = Number(value);
                      setManuallyChangedAmount(true);
                      if (validateAmount(newAmount, price)) {
                        setAmount(newAmount);
                        return true;
                      } else {
                        setAmount(getMaxAmount(price));
                        return false;
                      }
                    }}
                  />
                </div>
                <div className="finance__sliders">
                  <div className="finance__slider">
                    <p className="finance__subtitle-snd">
                      Porcentaje de prima
                    </p>
                    <div className="finance__range-wrapper">
                      <RangeSlider
                        labels={{
                          20: <div className={bem('finance__mark', {hasActivePromo})}>20%</div>,
                          30: <div className="finance__mark">30%</div>,
                          50: <div className="finance__mark">50%</div>,
                          80: <div className="finance__mark">80%</div>,
                        }}
                        value={prime}
                        onChange={value => {
                          changePrimePercentage(value);
                        }}
                        min={20}
                        max={MAX_FINANCING_PERCENTAGE}
                      />
                    </div>
                  </div>
                  <div className="finance__slider">
                    <p className="finance__subtitle-snd">
                      Plazo en meses
                    </p>
                    <div className="finance__range-wrapper">
                      <RangeSlider
                        tooltip={false}
                        labels={{
                          1: <div className="finance__mark">48</div>,
                          2: <div className="finance__mark">60</div>,
                          3: <div className="finance__mark">72</div>,
                          4: <div className="finance__mark">84</div>,
                          5: <div className={bem('finance__mark', {hasActivePromo})}>96</div>,
                        }}
                        value={plazoMatch[plazo] || 5}
                        onChange={value => {
                          const index = Object.keys(plazoMatch).filter(x => plazoMatch[x] === value);
                          if (!index.length) {
                            return;
                          }
                          changePlazo(index[0]);
                        }}
                        min={1}
                        max={5}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="finance__section">
              <h4 className="finance__title finance__double-divider">
                Seleccioná la opción que mejor se adapte a vos.
              </h4>
              <div className="finance__options">
                <FinanceOptions
                  amountOriginal={amountOriginal}
                  car={car}
                  plazo={plazo}
                  financeOptions={financeOptions}
                  prime={prime}
                  financingAmount={amount}
                  selectFinancingOption={executeSelect}
                  selectedFinancingOptionsNumber={
                    selectedFinancingOptionsNumber
                  }
                  resetAdd={(toAdd) => {
                    const selected = financeOptions.find(x => x.isSelected);
                    if (selected) {
                      executeSelect(selected);
                    }
                    executeSelect(toAdd);
                    //
                  }}
                />
              </div>
            </div>
          </div>
        </StyleRoot>
      )}
        <NavigationBar
          back={`/${tipo}/${modelo}/${version}/${anio}/${color}/seguros/`}
          next={`/${tipo}/${modelo}/${version}/${anio}/${color}/resumen/`}
        />
      </div>
    </PersonalizationContainer>
  );
};

export default Finance;
